/**
 * BJAF shipping modal + live cutoff countdown.
 * Reusable via <bjaf-shipping-cutoff> on PDP / checkout later.
 */
(function () {
  'use strict';

  function getZonedParts(date, timeZone) {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: false,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    }).formatToParts(date);

    const values = {};
    parts.forEach(function (part) {
      if (part.type !== 'literal') {
        values[part.type] = parseInt(part.value, 10);
      }
    });
    return values;
  }

  function zonedTimeToUtcMs(year, month, day, hour, minute, second, timeZone) {
    let guess = Date.UTC(year, month - 1, day, hour, minute, second);

    for (let i = 0; i < 3; i += 1) {
      const parts = getZonedParts(new Date(guess), timeZone);
      const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
      const offset = guess - asUtc;
      guess = Date.UTC(year, month - 1, day, hour, minute, second) + offset;
    }

    return guess;
  }

  // Day-of-week only depends on the calendar date, not the time zone offset,
  // so a plain UTC-midnight Date for that y/m/d gives the correct weekday.
  function isWeekendDate(year, month, day) {
    const dow = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
    return dow === 0 || dow === 6;
  }

  function nextWeekday(year, month, day) {
    let d = new Date(Date.UTC(year, month - 1, day));
    do {
      d = new Date(d.getTime() + 86400000);
    } while (d.getUTCDay() === 0 || d.getUTCDay() === 6);
    return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
  }

  function previousWeekday(year, month, day) {
    let d = new Date(Date.UTC(year, month - 1, day));
    do {
      d = new Date(d.getTime() - 86400000);
    } while (d.getUTCDay() === 0 || d.getUTCDay() === 6);
    return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
  }

  function getCutoffState(now, timeZone, cutoffHour, cutoffMinute) {
    const parts = getZonedParts(now, timeZone);
    const secondsNow = parts.hour * 3600 + parts.minute * 60 + parts.second;
    const cutoffSeconds = cutoffHour * 3600 + cutoffMinute * 60;

    // The cutoff (theme setting, 15:00) only exists Mon-Fri. On Saturday/Sunday, or after
    // Friday's cutoff, the countdown keeps live-ticking, just toward the
    // next weekday's cutoff instead of "tomorrow" (so Friday after the cutoff and
    // all of Saturday/Sunday count down to Monday, not Saturday).
    const todayIsWeekend = isWeekendDate(parts.year, parts.month, parts.day);
    const pastCutoffToday = !todayIsWeekend && secondsNow >= cutoffSeconds;

    let targetDay = parts.day;
    let targetMonth = parts.month;
    let targetYear = parts.year;

    if (todayIsWeekend || pastCutoffToday) {
      const next = nextWeekday(parts.year, parts.month, parts.day);
      targetYear = next.year;
      targetMonth = next.month;
      targetDay = next.day;
    }

    const cutoffMs = zonedTimeToUtcMs(targetYear, targetMonth, targetDay, cutoffHour, cutoffMinute, 0, timeZone);
    const remainingMs = Math.max(0, cutoffMs - now.getTime());

    let progress = 100;
    if (!todayIsWeekend && secondsNow < cutoffSeconds) {
      // Weekday before cutoff: fill midnight → today's cutoff.
      progress = (secondsNow / cutoffSeconds) * 100;
    } else {
      // After cutoff / weekend: fill across the wait window (last weekday
      // cutoff → next weekday cutoff). Was hard-coded to 0, which left the
      // purple bar empty while the countdown still showed "2 d 11 h left".
      let startYear = parts.year;
      let startMonth = parts.month;
      let startDay = parts.day;
      if (todayIsWeekend) {
        const prev = previousWeekday(parts.year, parts.month, parts.day);
        startYear = prev.year;
        startMonth = prev.month;
        startDay = prev.day;
      }
      const startMs = zonedTimeToUtcMs(
        startYear,
        startMonth,
        startDay,
        cutoffHour,
        cutoffMinute,
        0,
        timeZone
      );
      const totalMs = Math.max(1, cutoffMs - startMs);
      const elapsedMs = Math.max(0, now.getTime() - startMs);
      progress = Math.min(100, (elapsedMs / totalMs) * 100);
    }

    // Ships today only on a weekday before the cutoff; otherwise on the target day (tomorrow, or Monday).
    const shipsToday = !todayIsWeekend && !pastCutoffToday;
    const tomorrow = new Date(Date.UTC(parts.year, parts.month - 1, parts.day) + 86400000);
    const shipsTomorrow = !shipsToday &&
      tomorrow.getUTCFullYear() === targetYear && tomorrow.getUTCMonth() + 1 === targetMonth && tomorrow.getUTCDate() === targetDay;

    return { remainingMs, progress, shipsToday, shipsTomorrow, shipYear: targetYear, shipMonth: targetMonth, shipDay: targetDay };
  }

  // After the cutoff the "ships today" sentence ([data-bjaf-dispatch-today]) is swapped for
  // [data-bjaf-dispatch-later]: its data-tomorrow text, or data-day with [day] = the weekday name in the page language.
  function updateDispatchText(root, state) {
    const todayEl = root.querySelector('[data-bjaf-dispatch-today]');
    const laterEl = root.querySelector('[data-bjaf-dispatch-later]');
    if (!todayEl || !laterEl) return;

    todayEl.hidden = !state.shipsToday;
    laterEl.hidden = state.shipsToday;
    todayEl.style.display = state.shipsToday ? '' : 'none';
    laterEl.style.display = state.shipsToday ? 'none' : '';
    if (state.shipsToday) return;

    let text = laterEl.getAttribute('data-tomorrow') || '';
    if (!state.shipsTomorrow || !text) {
      let dayName = '';
      try {
        dayName = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { weekday: 'long', timeZone: 'UTC' })
          .format(new Date(Date.UTC(state.shipYear, state.shipMonth - 1, state.shipDay, 12)));
      } catch (e) {
        dayName = '';
      }
      text = (laterEl.getAttribute('data-day') || '').split('[day]').join(dayName);
    }
    if (laterEl.textContent !== text) laterEl.textContent = text;
  }

  function formatCountdown(ms) {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    // Weekend/after-hours countdowns can span more than a day (e.g. Friday
    // evening -> Monday 16:00), so a "d" unit is needed once we cross 24h.
    if (days > 0) {
      return days + ' d ' + hours + ' h';
    }

    if (hours > 0) {
      return hours + ' h ' + minutes + ' m';
    }

    if (minutes > 0) {
      return minutes + ' m ' + seconds + ' s';
    }

    return seconds + ' s';
  }

  var barAnimating = false;

  function animateShippingBar(root) {
    var barEl = root.querySelector('[data-bjaf-shipping-bar]');
    if (!barEl) {
      return;
    }

    var cutoffHour = parseInt(root.dataset.cutoffHour || '16', 10);
    var cutoffMinute = parseInt(root.dataset.cutoffMinute || '0', 10);
    var timeZone = root.dataset.timezone || 'Europe/Copenhagen';
    var state = getCutoffState(new Date(), timeZone, cutoffHour, cutoffMinute);

    barAnimating = true;
    barEl.style.transition = 'none';
    barEl.classList.remove('is-opening');
    barEl.style.width = '100%';
    void barEl.offsetWidth;
    barEl.style.transition = '';
    barEl.classList.add('is-opening');
    barEl.style.width = state.progress.toFixed(3) + '%';

    function finishAnimation() {
      barEl.classList.remove('is-opening');
      barAnimating = false;
    }

    barEl.addEventListener('transitionend', function onEnd(event) {
      if (event.propertyName !== 'width') {
        return;
      }

      barEl.removeEventListener('transitionend', onEnd);
      finishAnimation();
    });

    window.setTimeout(finishAnimation, 900);
  }

  function initCutoff(root) {
    const cutoffHour = parseInt(root.dataset.cutoffHour || '16', 10);
    const cutoffMinute = parseInt(root.dataset.cutoffMinute || '0', 10);
    const timeZone = root.dataset.timezone || 'Europe/Copenhagen';
    const countdownEl = root.querySelector('[data-bjaf-shipping-countdown]');
    const barEl = root.querySelector('[data-bjaf-shipping-bar]');

    if (!countdownEl && !barEl) {
      return null;
    }

    function tick() {
      const state = getCutoffState(new Date(), timeZone, cutoffHour, cutoffMinute);

      if (countdownEl) {
        const countdown = formatCountdown(state.remainingMs);
        countdownEl.textContent = root.matches('.bjaf-pdp-dispatch') ? countdown + ' ' : countdown;
      }

      updateDispatchText(root, state);

      if (barEl && !barAnimating && !barEl.classList.contains('is-opening')) {
        barEl.style.width = state.progress.toFixed(3) + '%';
      }
    }

    tick();
    return window.setInterval(tick, 1000);
  }

  class BjafShippingCutoff extends HTMLElement {
    connectedCallback() {
      this._timer = initCutoff(this);
    }

    disconnectedCallback() {
      if (this._timer) {
        window.clearInterval(this._timer);
      }
    }
  }

  if (!window.customElements.get('bjaf-shipping-cutoff')) {
    window.customElements.define('bjaf-shipping-cutoff', BjafShippingCutoff);
  }

  var modal = document.getElementById('shipModal');
  if (!modal) {
    return;
  }

  var lastFocus = null;
  var lockY = 0;

  function pinCartDrawerOpen() {
    var cart = document.querySelector('cart-drawer');
    if (!cart || !cart.open || cart._bjafShippingPinned) {
      return;
    }

    // Clicks / focus inside #shipModal are outside the cart drawer, so Impact's
    // focus-trap treats them as outside-click and closes the cart. Keep it open
    // for the life of the shipping modal (Mikkel: drawer must stay open).
    cart._bjafShippingPinned = true;
    cart.setAttribute('data-bjaf-shipping-open', '');
    cart._bjafOriginalHide = cart.hide.bind(cart);
    cart.hide = function () {
      if (cart.hasAttribute('data-bjaf-shipping-open')) {
        return Promise.resolve();
      }
      return cart._bjafOriginalHide();
    };
  }

  function unpinCartDrawer() {
    var cart = document.querySelector('cart-drawer');
    if (!cart || !cart._bjafShippingPinned) {
      return;
    }

    cart.removeAttribute('data-bjaf-shipping-open');
    if (cart._bjafOriginalHide) {
      cart.hide = cart._bjafOriginalHide;
      delete cart._bjafOriginalHide;
    }
    cart._bjafShippingPinned = false;
  }

  function openModal(trigger) {
    lastFocus = trigger || document.activeElement;
    pinCartDrawerOpen();
    modal.hidden = false;
    lockY = window.scrollY || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = (-lockY) + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';

    window.requestAnimationFrame(function () {
      modal.classList.add('in');

      modal.querySelectorAll('bjaf-shipping-cutoff, [data-bjaf-shipping-options]').forEach(function (root) {
        animateShippingBar(root);
      });
    });

    var closeButton = modal.querySelector('[data-close].bjaf-ship-x');
    if (closeButton) {
      closeButton.focus();
    }
  }

  function closeModal() {
    modal.classList.remove('in');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.overflow = '';
    window.scrollTo(0, lockY);

    var done = function () {
      modal.hidden = true;
      modal.removeEventListener('transitionend', done);
      unpinCartDrawer();
    };

    modal.addEventListener('transitionend', done);
    window.setTimeout(function () {
      if (!modal.hidden) {
        done();
      }
    }, 400);

    if (lastFocus && lastFocus.focus) {
      lastFocus.focus();
    }
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('.ship-link, [data-bjaf-shipping-open]');
    if (trigger) {
      event.preventDefault();
      openModal(trigger);
      return;
    }

    if (event.target.closest('[data-close]') && !modal.hidden) {
      event.preventDefault();
      closeModal();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && !modal.hidden) {
      closeModal();
    }
  });
})();
